import { useCallback, useEffect, useRef, useState } from "react";

export interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
  since: Date | null;
}

const OFFLINE_DEBOUNCE_MS = 3_000;
const ONLINE_DEBOUNCE_MS = 5_000;

export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [since, setSince] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOfflineRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        setIsOnline(true);
        setSince(new Date());
        if (wasOfflineRef.current) {
          setWasOffline(true);
          // Auto-clear wasOffline after consumers have had a chance to react
          setTimeout(() => setWasOffline(false), 5_000);
        }
        wasOfflineRef.current = false;
      }, ONLINE_DEBOUNCE_MS);
    };

    const handleOffline = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        setIsOnline(false);
        setSince(new Date());
        wasOfflineRef.current = true;
      }, OFFLINE_DEBOUNCE_MS);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      clearTimer();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [clearTimer]);

  return { isOnline, wasOffline, since };
};
